import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
 Box,
 Button,
 Flex,
 IconButton,
 Input,
 InputGroup,
 InputLeftElement,
 Select,
 Spinner,
 Table,
 Tbody,
 Td,
 Text,
 Th,
 Thead,
 Tooltip,
 Tr,
 useToast,
} from '@chakra-ui/react';
import { FiEdit2, FiEye, FiInbox, FiPlus, FiSearch, FiTrash2 } from 'react-icons/fi';
import { MainContent, PageHeaderStructure } from '../../components/Layout';
import { listSchedules, deleteSchedule } from '../../services/scheduleService';
import {
 BLUE,
 SCHEDULE_STATUS_OPTIONS,
 dueState,
 formatDate,
 progressOf,
 statusMeta,
} from './constants';
import { AvatarStack, FilterChip, ProgressBar, StatusPill } from './components';
import { AttentionModal } from '../../components/Modals';
import { useScheduleListSocket } from './useScheduleSocket';
import ScheduleFormModal from './ScheduleFormModal';
import { useSSO } from '../../context/SSO';

// Cronograma atrasado = fim vencido e ainda não concluído (reusa dueState).
const isOverdue = (s) => dueState(s.end_date, s.status) === 'overdue';

// Chips de status da faixa de filtro. 'ATRASADO' é derivado (não é status salvo).
const STATUS_CHIPS = [
 { value: '', label: 'Todos' },
 ...SCHEDULE_STATUS_OPTIONS,
 { value: 'ATRASADO', label: 'Atrasado' },
];


export default function Schedules() {
 const navigate = useNavigate();
 const toast = useToast();
 const { user } = useSSO();

 // AVALIADOR gerencia os cronogramas em que é vinculado, mas não cria do zero
 // (a vinculação é feita pelo MASTER/admin ao criar). Só MASTER cria por aqui.
 const roles = user?.user?.roles || [];
 const canCreate = roles.some((r) => r.name === 'MASTER');

 const [schedules, setSchedules] = useState([]);
 const [loading, setLoading] = useState(true);
 const [filters, setFilters] = useState({
  name: '',
  company: '',
  consultant: '',
  status: '',
  date_from: '',
  date_to: '',
 });
 const [formOpen, setFormOpen] = useState(false);
 const [editing, setEditing] = useState(null);
 const [toDelete, setToDelete] = useState(null);
 const [deleting, setDeleting] = useState(false);

 async function load(silent = false) {
  if (!silent) setLoading(true);
  try {
   const data = await listSchedules();
   setSchedules(Array.isArray(data) ? data : []);
  } catch (err) {
   toast({ title: 'Erro ao carregar cronogramas', status: 'error' });
  } finally {
   if (!silent) setLoading(false);
  }
 }

 useEffect(() => {
  load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []);

 // Realtime: atualiza a lista quando qualquer cronograma muda.
 useScheduleListSocket(() => load(true));

 const companyOptions = useMemo(() => {
  const map = new Map();
  schedules.forEach((s) => {
   if (s.company?.id_company) {
    map.set(s.company.id_company, s.company.fantasyName || s.company.name);
   }
  });
  return Array.from(map, ([value, label]) => ({ value, label }));
 }, [schedules]);

 const consultantOptions = useMemo(() => {
  const map = new Map();
  schedules.forEach((s) => {
   (s.consultants || []).forEach((c) => {
    if (c.id_user) map.set(c.id_user, c.username);
   });
  });
  return Array.from(map, ([value, label]) => ({ value, label }));
 }, [schedules]);

 const filtered = useMemo(() => {
  return schedules.filter((s) => {
   if (filters.name && !s.name?.toLowerCase().includes(filters.name.toLowerCase()))
    return false;
   if (filters.company && String(s.company?.id_company) !== String(filters.company))
    return false;
   if (
    filters.consultant &&
    !s.consultants?.some((c) => String(c.id_user) === String(filters.consultant))
   )
    return false;
   if (filters.status === 'ATRASADO') {
    if (!isOverdue(s)) return false;
   } else if (filters.status && s.status !== filters.status) return false;
   if (filters.date_from && s.end_date && s.end_date < filters.date_from) return false;
   if (filters.date_to && s.start_date && s.start_date > filters.date_to) return false;
   return true;
  });
 }, [schedules, filters]);

 async function confirmDelete() {
  if (!toDelete) return;
  setDeleting(true);
  try {
   await deleteSchedule(toDelete.id_schedule);
   toast({ title: 'Cronograma removido', status: 'success' });
   setToDelete(null);
   load();
  } catch (err) {
   toast({ title: 'Erro ao remover cronograma', status: 'error' });
  } finally {
   setDeleting(false);
  }
 }

 function openCreate() {
  setEditing(null);
  setFormOpen(true);
 }

 function openEdit(e, schedule) {
  e.stopPropagation();
  setEditing(schedule);
  setFormOpen(true);
 }

 return (
  <MainContent>
   <PageHeaderStructure
    title='Cronogramas'
    subtitle='Acompanhe atividades, prazos e responsáveis de cada empresa.'
   >
    {canCreate && (
     <Button variant='primary' leftIcon={<FiPlus />} onClick={openCreate}>
      Novo cronograma
     </Button>
    )}
   </PageHeaderStructure>

   {/* Chips de status */}
   <Flex gap='6px' mt='20px' flexWrap='wrap'>
    {STATUS_CHIPS.map((c) => (
     <FilterChip
      key={c.value || 'all'}
      label={c.label}
      active={filters.status === c.value}
      onClick={() => setFilters((f) => ({ ...f, status: c.value }))}
     />
    ))}
   </Flex>

   {/* Barra de filtros densa */}
   <Flex
    gap='10px'
    mt='12px'
    p='12px'
    bg='#fff'
    borderRadius='12px'
    border='1px solid #E7E9EE'
    boxShadow='0 1px 2px rgba(16,32,48,0.04)'
    flexWrap='wrap'
    alignItems='flex-end'
   >
    <Field label='Buscar' minW='190px' flex='1'>
     <InputGroup size='sm'>
      <InputLeftElement pointerEvents='none' color='gray.400'>
       <FiSearch />
      </InputLeftElement>
      <Input
       borderRadius='8px'
       value={filters.name}
       onChange={(e) => setFilters((f) => ({ ...f, name: e.target.value }))}
       placeholder='Nome do cronograma'
      />
     </InputGroup>
    </Field>
    <Field label='Empresa' minW='160px'>
     <Select
      size='sm'
      borderRadius='8px'
      value={filters.company}
      onChange={(e) => setFilters((f) => ({ ...f, company: e.target.value }))}
      placeholder='Todas'
     >
      {companyOptions.map((o) => (
       <option key={o.value} value={o.value}>
        {o.label}
       </option>
      ))}
     </Select>
    </Field>
    <Field label='Consultor' minW='160px'>
     <Select
      size='sm'
      borderRadius='8px'
      value={filters.consultant}
      onChange={(e) => setFilters((f) => ({ ...f, consultant: e.target.value }))}
      placeholder='Todos'
     >
      {consultantOptions.map((o) => (
       <option key={o.value} value={o.value}>
        {o.label}
       </option>
      ))}
     </Select>
    </Field>
    <Field label='De' minW='140px'>
     <Input
      size='sm'
      borderRadius='8px'
      type='date'
      value={filters.date_from}
      onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
     />
    </Field>
    <Field label='Até' minW='140px'>
     <Input
      size='sm'
      borderRadius='8px'
      type='date'
      value={filters.date_to}
      onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
     />
    </Field>
   </Flex>

   {/* Board/tabela */}
   {loading ? (
    <Flex justify='center' py='60px'>
     <Spinner size='lg' color={BLUE} thickness='3px' />
    </Flex>
   ) : filtered.length === 0 ? (
    <Flex
     direction='column'
     align='center'
     justify='center'
     py='60px'
     mt='16px'
     color='gray.400'
     bg='#fff'
     border='1px solid #E7E9EE'
     borderRadius='14px'
    >
     <FiInbox size={30} />
     <Text mt='10px' fontSize='sm'>
      Nenhum cronograma encontrado.
     </Text>
    </Flex>
   ) : (
    <Box
     mt='16px'
     bg='#fff'
     border='1px solid #E7E9EE'
     borderRadius='14px'
     boxShadow='0 1px 2px rgba(16,32,48,0.04)'
     overflowX='auto'
    >
     <Table size='sm' variant='simple'>
      <Thead bg='#F7F8FA'>
       <Tr>
        <Th borderColor='#EDEFF2'>Cronograma</Th>
        <Th borderColor='#EDEFF2'>Empresa</Th>
        <Th borderColor='#EDEFF2'>Período</Th>
        <Th borderColor='#EDEFF2' minW='170px'>Progresso</Th>
        <Th borderColor='#EDEFF2'>Consultores</Th>
        <Th borderColor='#EDEFF2' textAlign='right'>Ações</Th>
       </Tr>
      </Thead>
      <Tbody>
       {filtered.map((s) => (
        <ScheduleRow
         key={s.id_schedule}
         schedule={s}
         onOpen={() => navigate(`/cronogramas/${s.id_schedule}`)}
         onEdit={(e) => openEdit(e, s)}
         onDelete={(e) => {
          e.stopPropagation();
          setToDelete(s);
         }}
        />
       ))}
      </Tbody>
     </Table>
     <Flex align='center' justify='space-between' px='22px' py='12px' fontSize='12px' color='gray.500'>
      <Text>
       Mostrando {filtered.length} de {schedules.length} cronogramas
      </Text>
     </Flex>
    </Box>
   )}

   <ScheduleFormModal
    isOpen={formOpen}
    onClose={() => setFormOpen(false)}
    schedule={editing}
    onSaved={() => {
     setFormOpen(false);
     load();
    }}
   />

   <AttentionModal
    isOpen={!!toDelete}
    onClose={() => setToDelete(null)}
    submit={confirmDelete}
    isLoading={deleting}
    redType
    title='Remover cronograma?'
    content={toDelete?.name}
   />
  </MainContent>
 );
}

function Field({ label, children, ...rest }) {
 return (
  <Box {...rest}>
   <Text fontSize='10px' fontWeight='800' color='gray.500' mb='3px' letterSpacing='0.04em' textTransform='uppercase'>
    {label}
   </Text>
   {children}
  </Box>
 );
}

function ScheduleRow({ schedule, onOpen, onEdit, onDelete }) {
 const meta = statusMeta(schedule.status);
 const overdue = isOverdue(schedule);
 // A listagem não traz as atividades; o progresso vem calculado do backend.
 const prog = schedule.progress || progressOf(schedule.activities || []);
 return (
  <Tr transition='background .12s ease' _hover={{ bg: '#F3F7FB' }}>
   <Td borderColor='#F1F3F6'>
    <Flex align='center' gap='10px'>
     <Box w='4px' h='34px' borderRadius='3px' bg={meta.solid} flexShrink={0} />
     <Box minW={0}>
      <Text fontWeight='700' color='gray.800' noOfLines={1}>
       {schedule.name}
      </Text>
      <Box mt='4px'>
       <StatusPill status={schedule.status} />
      </Box>
     </Box>
    </Flex>
   </Td>
   <Td borderColor='#F1F3F6'>
    <Text fontSize='sm' color='gray.600' noOfLines={1}>
     {schedule.company?.fantasyName || schedule.company?.name || '—'}
    </Text>
   </Td>
   <Td borderColor='#F1F3F6' whiteSpace='nowrap'>
    <Text fontSize='xs' color={overdue ? '#C0392B' : 'gray.600'} fontWeight={overdue ? '700' : '400'}>
     {formatDate(schedule.start_date)} — {formatDate(schedule.end_date)}
    </Text>
   </Td>
   <Td borderColor='#F1F3F6'>
    <Flex align='center' gap='8px'>
     <Box flex='1' minW='90px'>
      <ProgressBar pct={prog.pct} height={6} />
     </Box>
     <Text fontSize='xs' fontWeight='800' color='gray.600' whiteSpace='nowrap'>
      {prog.done}/{prog.total}
     </Text>
    </Flex>
   </Td>
   <Td borderColor='#F1F3F6'>
    <AvatarStack people={schedule.consultants || []} size={24} />
   </Td>
   <Td borderColor='#F1F3F6' onClick={(e) => e.stopPropagation()}>
    <Flex gap='4px' justify='flex-end'>
     <Tooltip label='Abrir' hasArrow>
      <IconButton aria-label='Abrir' size='sm' variant='ghost' colorScheme='blue' icon={<FiEye />} onClick={onOpen} />
     </Tooltip>
     <Tooltip label='Editar' hasArrow>
      <IconButton aria-label='Editar' size='sm' variant='ghost' colorScheme='gray' icon={<FiEdit2 />} onClick={onEdit} />
     </Tooltip>
     <Tooltip label='Remover' hasArrow>
      <IconButton aria-label='Remover' size='sm' variant='ghost' colorScheme='red' icon={<FiTrash2 />} onClick={onDelete} />
     </Tooltip>
    </Flex>
   </Td>
  </Tr>
 );
}
